import {keepPreviousData, useQuery} from "@tanstack/react-query";

import {getPagination} from "@/src/modules/core/lib";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";

import {citationsService} from "../services";

type UseCitationsParams = {
  rows: number;
  page: number;
  filters?: Record<string, unknown>;
  sort?: string;
  enabled?: boolean;
};

export function useCitations({
  rows,
  page,
  filters = {},
  sort = "-citation_date",
  enabled = true,
}: UseCitationsParams) {
  const userProperty = useStoredUserProperty();
  const filter: Record<string, string | number> = {
    context_property_id: userProperty?.id ?? 0,
    ...Object.fromEntries(
      Object.entries(filters).filter(
        ([, v]) =>
          v !== undefined &&
          v !== null &&
          v !== "" &&
          !(Array.isArray(v) && v.length === 0),
      ) as [string, string | number][],
    ),
  };

  const options = {
    page: {limit: rows, offset: (page - 1) * rows},
    filter,
    sort,
  };

  const {data, isSuccess, isPending, isError, error, isFetching, refetch} = useQuery({
    queryKey: ["citations", options],
    queryFn: () => citationsService.getCitations(options),
    retry: false,
    enabled: enabled && Boolean(userProperty?.id),
    placeholderData: keepPreviousData,
  });

  const citations = isSuccess && data ? data.data : [];
  const totalRecords = data?.meta?.total ?? 0;
  const {start, end} = getPagination(totalRecords, rows, page);

  return {
    citations,
    totalRecords,
    start,
    end,
    isSuccess,
    isPending,
    isError,
    error,
    isFetching,
    refetch,
  };
}
